import { Injectable } from '@angular/core';
import {
  doc,
  getDoc,
  getCountFromServer,
  collection,
  writeBatch,
  onSnapshot,
  Unsubscribe,
  increment,
} from 'firebase/firestore';
import { db, auth } from '../firebase';

@Injectable({
  providedIn: 'root',
})
export class FollowService {
  async toggleFollow(targetUserId: string, isCurrentlyFollowing: boolean) {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Not authenticated');
    if (currentUser.uid === targetUserId) throw new Error('Cannot follow yourself');

    const batch = writeBatch(db);
    const currentUserRef = doc(db, 'accounts', currentUser.uid);
    const targetUserRef = doc(db, 'accounts', targetUserId);
    const followingRef = doc(db, `accounts/${currentUser.uid}/following/${targetUserId}`);
    const followerRef = doc(db, `accounts/${targetUserId}/followers/${currentUser.uid}`);

    if (isCurrentlyFollowing) {
      batch.delete(followingRef);
      batch.delete(followerRef);
      batch.set(currentUserRef, { followingCount: increment(-1) }, { merge: true });
      batch.set(targetUserRef, { followersCount: increment(-1) }, { merge: true });
    } else {
      batch.set(followingRef, { timestamp: new Date().toISOString() });
      batch.set(followerRef, { timestamp: new Date().toISOString() });
      batch.set(currentUserRef, { followingCount: increment(1) }, { merge: true });
      batch.set(targetUserRef, { followersCount: increment(1) }, { merge: true });
    }

    await batch.commit();
  }

  async checkIsFollowing(targetUserId: string): Promise<boolean> {
    const currentUser = auth.currentUser;
    if (!currentUser) return false;

    const followingRef = doc(db, `accounts/${currentUser.uid}/following/${targetUserId}`);
    const snap = await getDoc(followingRef);
    return snap.exists();
  }

  // Real-time onSnapshot listeners for followers and following counts
  subscribeFollowStats(
    userId: string,
    callback: (stats: { followers: number; following: number }) => void
  ): Unsubscribe {
    const userRef = doc(db, `accounts/${userId}`);
    const followersRef = collection(db, `accounts/${userId}/followers`);
    const followingRef = collection(db, `accounts/${userId}/following`);

    let currentStats = { followers: 0, following: 0 };

    const updateAndEmit = (partial: Partial<{ followers: number; following: number }>) => {
      currentStats = { ...currentStats, ...partial };
      callback({
        followers: Math.max(0, currentStats.followers || 0),
        following: Math.max(0, currentStats.following || 0),
      });
    };

    const unsubUser = onSnapshot(
      userRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          if (typeof data['followersCount'] === 'number') {
            updateAndEmit({ followers: data['followersCount'] });
          }
          if (typeof data['followingCount'] === 'number') {
            updateAndEmit({ following: data['followingCount'] });
          }
        }
      },
      (err) => console.warn('User follow stats listener warning:', err)
    );

    const unsubFollowers = onSnapshot(
      followersRef,
      (snapshot) => {
        updateAndEmit({ followers: snapshot.size });
      },
      (err) => console.warn('Followers listener warning:', err)
    );

    const unsubFollowing = onSnapshot(
      followingRef,
      (snapshot) => {
        updateAndEmit({ following: snapshot.size });
      },
      (err) => console.warn('Following listener warning:', err)
    );

    return () => {
      unsubUser();
      unsubFollowers();
      unsubFollowing();
    };
  }

  subscribeFollowersCount(userId: string, callback: (count: number) => void): Unsubscribe {
    const followersRef = collection(db, `accounts/${userId}/followers`);
    return onSnapshot(
      followersRef,
      (snapshot) => {
        callback(snapshot.size);
      },
      (err) => console.warn('Followers count error:', err)
    );
  }

  subscribeFollowingCount(userId: string, callback: (count: number) => void): Unsubscribe {
    const followingRef = collection(db, `accounts/${userId}/following`);
    return onSnapshot(
      followingRef,
      (snapshot) => {
        callback(snapshot.size);
      },
      (err) => console.warn('Following count error:', err)
    );
  }

  async getFollowStats(userId: string) {
    const userRef = doc(db, `accounts/${userId}`);
    const snap = await getDoc(userRef);
    if (snap.exists()) {
      const data = snap.data();
      if (typeof data['followersCount'] === 'number' && typeof data['followingCount'] === 'number') {
        return {
          followers: Math.max(0, data['followersCount']),
          following: Math.max(0, data['followingCount']),
        };
      }
    }

    const followersRef = collection(db, `accounts/${userId}/followers`);
    const followingRef = collection(db, `accounts/${userId}/following`);

    const [followersSnap, followingSnap] = await Promise.all([
      getCountFromServer(followersRef),
      getCountFromServer(followingRef),
    ]);

    return {
      followers: followersSnap.data().count,
      following: followingSnap.data().count,
    };
  }
}
